import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { DISPLAY_UNIT_ENUM } from '~/modules/resourcing/common/enums';
import {
  useResourcingChartToolbarContext,
  useRefetchQueriesOnTaskAllocationModification
} from '~/modules/projects/resourcing-plan/hooks';
import {
  updateTaskResourceAllocationCacheOnRemove,
  useResourceTimelineEditorChangeHandlers
} from '~/modules/resourcing/common/hooks';
import { TaskAllocationTimelineBlocks } from '~/modules/resourcing/common/components/ResourceRequestUserAllocationBlock';
import {
  TaskAllocationTimelineEditorOverlay,
  ResourceAllocationChangeSnackBar,
  UserRoleActualsTimeLineRow
} from '~/modules/resourcing/common/components';
import {
  getUserActualTotalHoursByPeriod,
  getUserAllocatedTotalHoursPeriod
} from '~/modules/resourcing/util';
import {
  useUpdateTaskResourceUserAllocation,
  useCreateTaskResourceUserAllocation
} from '~/modules/tasks/components/TaskDrawer/common/hooks';
import { useTaskAssignmentTimelineEditorChangeHandlers } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceAllocationChart/hooks';
import { useDialogState } from '~/modules/common/hooks';
import { useUserAllocationsSummaryContext } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceAllocationChart/components/ResourceAllocationChartRow/UserAllocationsSummaryContext.js';
import { RangeBoundaries } from '~/modules/common/charts/timeline/components';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { RemoveTaskAllocationConfirmationDialog } from '~/modules/projects/resourcing-plan/components';
import { RemoveTaskAssignmentConfirmationDialog } from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/components/RemoveTaskAssignmentDialog';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden'
  },
  actualsRoot: ({ timelineRowCount }) => ({
    minHeight: (timelineRowCount + 1) * theme.spacing(4) + 4
  })
}));

const mapTaskAllocationToAllocationTimeline = ({
  user,
  userScheduleDetails,
  taskResourceUserAllocation = {}
}) => {
  const { displayText, id, uri } = user;
  const { scheduleDurationByPeriodMap } = userScheduleDetails;

  return {
    user: {
      id,
      uri,
      scheduleDurationByPeriodMap,
      displayText,
      user: {
        id,
        uri,
        displayText
      }
    },
    ...taskResourceUserAllocation
  };
};

export const getOverDistributedDisplayProperties = ({
  userOverDistributedPeriodsMap
}) => ({ periodStart }) => ({
  isOverDistributed: Boolean(userOverDistributedPeriodsMap[periodStart.toISO()])
});

const getActualSummaryBlockPopupProps = ({
  userActualTotalHours,
  userAllocatedTotalHours
}) => ({
  userActualTotalHours,
  userAllocatedTotalHours
});

export const TaskAssignmentTimeline = ({
  chartDisplayPeriods,
  handleRemoveTaskAssignment,
  user,
  taskActualSeriesData,
  taskResourceEstimate,
  taskResourceUserAllocation,
  userScheduleDetails
}) => {
  const classes = useStyles({
    timelineRowCount: Boolean(
      taskResourceUserAllocation || taskActualSeriesData
    )
  });

  const {
    scale,
    chartDates,
    dateRange,
    displayUnit,
    isResourceActualModeEnabled
  } = useResourcingChartToolbarContext();
  const {
    project,
    projectObjectPermissions: {
      canEditTasks,
      canViewCost,
      resourceAllocationActions
    }
  } = useProjectContext();
  const { id: projectId, startDate, endDate } = project;
  const [snackBarState, setSnackBarState] = useState({
    open: false,
    message: ''
  });

  const {
    loadingResourceAllocation,
    resourceAllocationScheduleRules,
    userOverDistributedPeriodsMap,
    userTaskAllocationsSummaryLoading,
    userTaskAllocationsSummaryScheduleRules,
    userCostRateDetails,
    handleRemoveResourceAllocation
  } = useUserAllocationsSummaryContext();

  const updateTaskResourceUserAllocation = useUpdateTaskResourceUserAllocation();
  const createTaskResourceUserAllocation = useCreateTaskResourceUserAllocation();

  const [allocationPeriodEditTarget, setAllocationPeriodEditTarget] = useState(
    null
  );

  const {
    handleAllocationPeriodClick,
    handleAllocationPeriodClose,
    setPreviousPeriod,
    setNextPeriod
  } = useResourceTimelineEditorChangeHandlers({
    allocationPeriodEditTarget,
    chartDates,
    isPercentageMode: displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE,
    setAllocationPeriodEditTarget,
    resourceAllocation: taskResourceUserAllocation || {}
  });

  const {
    open: isRemoveTaskAssignmentDialogOpen,
    openDialog: openRemoveTaskAssignmentDialog,
    closeDialog: closeRemoveTaskAssignmentDialog
  } = useDialogState(false);

  const {
    open: isRemoveTaskAllocationDialogOpen,
    openDialog: openRemoveTaskAllocationDialog,
    closeDialog: closeRemoveTaskAllocationDialog
  } = useDialogState(false);

  const {
    onPeriodClose,
    onAllocationChange
  } = useTaskAssignmentTimelineEditorChangeHandlers({
    updateTaskResourceUserAllocation,
    createTaskResourceUserAllocation,
    taskResourceUserAllocation,
    taskResourceEstimate,
    projectId,
    userId: user.id,
    openRemoveTaskAllocationDialog,
    setSnackBarState,
    resourceAllocationActions
  });

  const mappedTaskAllocation = mapTaskAllocationToAllocationTimeline({
    user,
    userScheduleDetails,
    taskResourceUserAllocation
  });

  const refetchQueries = useRefetchQueriesOnTaskAllocationModification({
    userId: user.id,
    projectId
  });

  const userAllocatedTotalHours = isResourceActualModeEnabled
    ? getUserAllocatedTotalHoursPeriod({
        allocations: taskResourceUserAllocation
          ? [taskResourceUserAllocation]
          : [],
        chartDisplayDateRange: dateRange,
        chartDisplayPeriods
      })
    : [];

  const userActualTotalHours = getUserActualTotalHoursByPeriod({
    actualSeries: taskActualSeriesData ? [taskActualSeriesData] : [],
    scale
  });

  const taskDisplayText = taskActualSeriesData?.task?.displayText || 'noTask';
  const isTimelineEditable =
    canEditTasks && !taskResourceEstimate?.isUnassigned;

  return (
    <div
      className={classNames(classes.root, {
        [classes.actualsRoot]: isResourceActualModeEnabled
      })}
    >
      <RangeBoundaries
        chartStartDate={dateRange.startDate}
        scale={scale}
        start={startDate}
        end={endDate}
      />
      <TaskAllocationTimelineBlocks
        taskAllocation={mappedTaskAllocation}
        chartDisplayPeriods={chartDisplayPeriods}
        scale={scale}
        isEditable={isTimelineEditable}
        chartDisplayDateRange={dateRange}
        onAllocationChange={onAllocationChange}
        handleAllocationPeriodClick={handleAllocationPeriodClick}
        getCustomDisplayPeriodProps={
          taskResourceUserAllocation &&
          getOverDistributedDisplayProperties({
            userOverDistributedPeriodsMap
          })
        }
        userCostRateDetails={userCostRateDetails}
        dataQeId={`${user.displayText}_${taskResourceEstimate?.task.displayText}`}
      />
      {isResourceActualModeEnabled && (
        <UserRoleActualsTimeLineRow
          actualSummaryBlockPopupProps={getActualSummaryBlockPopupProps({
            userActualTotalHours,
            userAllocatedTotalHours
          })}
          actualsData={taskActualSeriesData}
          chartDisplayDateRange={dateRange}
          chartDisplayPeriods={chartDisplayPeriods}
          dataQeId={`${taskDisplayText}-UserRoleActualsRow`}
          topOffSet={40}
          resourceAllocations={
            taskResourceUserAllocation && [taskResourceUserAllocation]
          }
          scale={scale}
          user={user}
          userScheduleDetails={userScheduleDetails}
        />
      )}
      {isTimelineEditable && allocationPeriodEditTarget?.anchorEl && (
        <TaskAllocationTimelineEditorOverlay
          chartDisplayDateRange={dateRange}
          scale={scale}
          loadingResourceAllocation={loadingResourceAllocation}
          resourceAllocationScheduleRules={resourceAllocationScheduleRules}
          userTaskAllocationsSummaryLoading={userTaskAllocationsSummaryLoading}
          userTaskAllocationsSummaryScheduleRules={
            userTaskAllocationsSummaryScheduleRules
          }
          allocationPeriodEditTarget={allocationPeriodEditTarget}
          onChange={onPeriodClose}
          handleAllocationPeriodClose={handleAllocationPeriodClose}
          taskResourceUserAllocation={mappedTaskAllocation}
          setNextPeriod={setNextPeriod}
          setPreviousPeriod={setPreviousPeriod}
          userId={user.id}
          resourceAvailabilitySummaryByPeriodMap={
            userScheduleDetails.resourceAvailabilitySummaryByPeriodMap
          }
        />
      )}

      {isRemoveTaskAllocationDialogOpen && (
        <RemoveTaskAllocationConfirmationDialog
          projectId={projectId}
          task={taskResourceEstimate.task}
          user={user}
          open={isRemoveTaskAllocationDialogOpen}
          onClose={closeRemoveTaskAllocationDialog}
          openRemoveTaskAssignmentDialog={openRemoveTaskAssignmentDialog}
          onRemoveTaskAllocationSuccess={closeRemoveTaskAllocationDialog}
          refetchQueries={refetchQueries}
          handleRemoveResourceAllocation={handleRemoveResourceAllocation}
        />
      )}
      {isRemoveTaskAssignmentDialogOpen && (
        <RemoveTaskAssignmentConfirmationDialog
          projectId={projectId}
          task={taskResourceEstimate.task}
          user={user}
          open={isRemoveTaskAssignmentDialogOpen}
          onClose={closeRemoveTaskAssignmentDialog}
          onRemoveTaskAssignmentSuccess={handleRemoveTaskAssignment}
          refetchQueries={refetchQueries}
          canViewCost={canViewCost}
          updateCache={updateTaskResourceAllocationCacheOnRemove}
        />
      )}

      {snackBarState.open && (
        <ResourceAllocationChangeSnackBar
          snackBarState={snackBarState}
          setSnackBarState={setSnackBarState}
        />
      )}
    </div>
  );
};

TaskAssignmentTimeline.propTypes = {
  taskActualSeriesData: PropTypes.object,
  taskResourceUserAllocation: PropTypes.object,
  user: PropTypes.object,
  userScheduleDetails: PropTypes.object,
  chartDisplayPeriods: PropTypes.array.isRequired,
  taskResourceEstimate: PropTypes.object,
  handleRemoveTaskAssignment: PropTypes.func
};

export default TaskAssignmentTimeline;
